/** Why a release or a reopen cannot happen now (tenant retirement CONTRACT §8.6): one line per
 *  refusal (`{code, detail, …extras}`), each refusal once (the backend may name one twice), with
 *  what it needs, from the refusal's extras (`TenantLifecycleError`) when it carries them —
 *
 *  - `release_too_early`: the waiting period (`min_closed_days`) from the close (`closed_at`) to
 *    the earliest release (`earliest_release_at`);
 *  - `offboarding_incomplete`: the items holding the release back (`items`, each with its status)
 *    and a link to the tenant's Offboarding tab (for `console_preflight_view`, which draws it);
 *  - `work_running`: the work that has to finish first (`what`);
 *  - `code_taken`: the tenants holding the code now (`holders`), each linked.
 *
 *  The state, the release preview and an error body all carry their refusals this way. */
import { Box, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { STATUS_LABELS } from '@/features/preflight/status';
import { When } from '@/features/tour-operators/sandbox/When';
import { usePermissions } from '@/hooks/usePermissions';

/** A lifecycle refusal as the state, the preview or an error body carry it (its extras, such as
 *  `items` or `holders`, are read when present). */
export interface LifecycleBlocker {
  code: string;
  detail: string;
}

const extra = (b: LifecycleBlocker, key: string): unknown => (b as unknown as Record<string, unknown>)[key];

interface OffboardingItemRef {
  action: string;
  title: string;
  status: string;
}

interface Holder {
  id: number;
  code: string;
}

function itemsOf(b: LifecycleBlocker): OffboardingItemRef[] {
  const items = extra(b, 'items');
  if (!Array.isArray(items)) return [];
  return items.flatMap((i): OffboardingItemRef[] =>
    i && typeof i === 'object' && typeof (i as OffboardingItemRef).action === 'string'
      ? [
          {
            action: (i as OffboardingItemRef).action,
            title: String((i as OffboardingItemRef).title ?? (i as OffboardingItemRef).action),
            status: String((i as OffboardingItemRef).status ?? ''),
          },
        ]
      : [],
  );
}

function holdersOf(b: LifecycleBlocker): Holder[] {
  const holders = extra(b, 'holders');
  if (!Array.isArray(holders)) return [];
  return holders.flatMap((h): Holder[] =>
    h && typeof h === 'object' && typeof (h as Holder).id === 'number'
      ? [{ id: (h as Holder).id, code: String((h as Holder).code ?? '') }]
      : [],
  );
}

/** `work_running`'s `what`: the running import runs, background tasks and catalog lanes. */
function whatOf(b: LifecycleBlocker): string[] {
  const what = extra(b, 'what');
  return Array.isArray(what)
    ? what.filter((w): w is string => typeof w === 'string' && w !== '')
    : [];
}

interface WaitingPeriod {
  closedAt: string | null;
  earliestReleaseAt: string | null;
  minClosedDays: number | null;
}

/** `release_too_early`'s dates, or null when it carries none of them. */
function waitingPeriodOf(b: LifecycleBlocker): WaitingPeriod | null {
  const str = (key: string) => {
    const v = extra(b, key);
    return typeof v === 'string' && v !== '' ? v : null;
  };
  const days = extra(b, 'min_closed_days');
  const period = {
    closedAt: str('closed_at'),
    earliestReleaseAt: str('earliest_release_at'),
    minClosedDays: typeof days === 'number' ? days : null,
  };
  return period.closedAt || period.earliestReleaseAt || period.minClosedDays != null
    ? period
    : null;
}

/** Each refusal once: the backend may name the same one twice. */
export function distinctBlockers<B extends LifecycleBlocker>(blockers: readonly B[]): B[] {
  return [...new Map(blockers.map((b) => [`${b.code}:${b.detail}`, b])).values()];
}

function statusWords(status: string): string {
  if (status === 'missing') return 'not on the tenant';
  return status in STATUS_LABELS
    ? STATUS_LABELS[status as keyof typeof STATUS_LABELS].toLowerCase()
    : status;
}

export function LifecycleBlockerList({
  topId,
  blockers,
  label,
}: {
  topId: number;
  blockers: readonly LifecycleBlocker[];
  /** The list's accessible name, e.g. "What blocks a release now". */
  label: string;
}) {
  const { hasPerm } = usePermissions();
  const list = distinctBlockers(blockers);
  if (list.length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }} aria-label={label}>
      {list.map((b) => {
        const items = b.code === 'offboarding_incomplete' ? itemsOf(b) : [];
        const holders = b.code === 'code_taken' ? holdersOf(b) : [];
        const what = b.code === 'work_running' ? whatOf(b) : [];
        const period = b.code === 'release_too_early' ? waitingPeriodOf(b) : null;
        return (
          <li key={`${b.code}:${b.detail}`} data-blocker={b.code}>
            {b.detail}
            {period && (
              <Box data-needs>
                Needs the{period.minClosedDays != null ? ` ${period.minClosedDays}-day` : ''}{' '}
                waiting period over
                {period.closedAt && (
                  <>
                    : closed <When iso={period.closedAt} />
                  </>
                )}
                {period.earliestReleaseAt && (
                  <>
                    {period.closedAt ? ', releasable ' : ': releasable '}
                    <When iso={period.earliestReleaseAt} />
                  </>
                )}
                .
              </Box>
            )}
            {items.length > 0 && (
              <>
                <Box data-needs>Needs each item done, or skipped with a reason:</Box>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5 }}
                  aria-label="Offboarding items in the way"
                >
                  {items.map((i) => (
                    <li key={i.action}>
                      {i.title}
                      {i.status && ` — ${statusWords(i.status)}`}
                    </li>
                  ))}
                </Box>
              </>
            )}
            {what.length > 0 && (
              <>
                <Box data-needs>Needs this to finish first:</Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }} aria-label="Work still running">
                  {what.map((w, n) => (
                    <li key={`${n}:${w}`}>{w}</li>
                  ))}
                </Box>
              </>
            )}
            {b.code === 'offboarding_incomplete' && hasPerm('console_preflight_view') && (
              <Box>
                <Link component={RouterLink} to={`/tour-operators/${topId}/offboarding`}>
                  Open the Offboarding tab
                </Link>
              </Box>
            )}
            {holders.length > 0 && (
              <Box>
                Held by{' '}
                {holders.map((h, n) => (
                  <Box component="span" key={h.id}>
                    {n > 0 && ', '}
                    <Link
                      component={RouterLink}
                      to={`/tour-operators/${h.id}`}
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {h.code || `#${h.id}`}
                    </Link>
                  </Box>
                ))}
                .
              </Box>
            )}
          </li>
        );
      })}
    </Box>
  );
}
